/*
 * Adapptio Editor
 *
 * @package editor-ui
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useState } from "react";
import SVG from "react-inlinesvg";

/**
 * Svg Image props
 */
export interface ISvgImageProps {
	/** Source */
	source: string;
	/** If to show loading placeholder (defaults to true) */
	showLoader?: boolean;
	/** If to cache requests (defaults to true) */
	cacheRequests?: boolean;
}

/**
 * Svg Image component
 */
const SvgImage: React.FC<ISvgImageProps> = ({ source, showLoader, cacheRequests }) => {
	const [ currentSource, setCurrentSource ] = useState(source);
	const [ wasLoaded, setWasLoaded ] = useState(false);

	const handleLoad = useCallback(() => {
		setWasLoaded(true);
	}, []);

	const handleError = useCallback((err) => {
		setWasLoaded(true);
		console.warn("Failed to load SVG icon:", err);
	}, []);

	useEffect(() => {
		if (source !== currentSource && wasLoaded) {
			setCurrentSource(source);
			setWasLoaded(false);
		}
	}, [ source, currentSource, wasLoaded ]);

	return (
		<SVG
			src={currentSource}
			cacheRequests={cacheRequests}
			className="edt-svg-image edt-svg-image--ready"
			loader={
				showLoader !== false ? (
					<span className="edt-svg-image edt-svg-image--loading">&nbsp;</span>
				) : undefined
			}
			onLoad={handleLoad}
			onError={handleError}
		/>
	);
};

const SvgImageMemo = React.memo(SvgImage);

export { SvgImageMemo as SvgImage };
